/**
 * Adapts existing Tabulator column definitions to v-data-table headers.
 *
 * The whole point is that the ~46 model files under the services' models
 * directories keep their current shape. Nothing here rewrites a model; the original
 * definition rides along on `header._def` so cell rendering, export, and
 * filtering can still reach it.
 */

function slugify(text) {
  return String(text || "col")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const ALIGN_MAP = { left: "start", right: "end", center: "center" };

/** Tabulator's `frozen: true` becomes a sticky-left column. */
function isFrozen(def) {
  return def.frozen === true;
}

/** Row-selection columns become v-data-table's built-in `show-select`. */
function isRowSelection(def) {
  return def.formatter === "rowSelection" || def.titleFormatter === "rowSelection";
}

function parseWidth(w) {
  if (w === null || w === undefined || w === "") return null;
  const n = parseInt(w, 10);
  return Number.isNaN(n) ? null : n;
}

/**
 * @param {Array} defs      model.browse.table.headers (Tabulator column defs)
 * @param {object} [opts]
 * @param {string[]} [opts.visibleKeys]  keys currently visible; undefined = use defaults
 * @param {object} [opts.widths]         { field: px } overrides
 * @returns {{headers: Array, showSelect: boolean, selectable: Array, defaultVisible: string[]}}
 */
export function adaptColumns(defs, opts) {
  const options = opts || {};
  const source = Array.isArray(defs) ? defs : [];

  let showSelect = false;
  const all = [];
  const selectable = [];
  const defaultVisible = [];

  source.forEach((def, i) => {
    if (!def) return;

    if (isRowSelection(def)) {
      // `visible: false` on these columns means the page doesn't want checkboxes.
      if (def.visible !== false) showSelect = true;
      return;
    }

    // Columns with no `field` (Index, Action, Next Action) still need a stable
    // identity — visibility and sticky offsets key off it.
    const key = def.field || `__${slugify(def.title)}_${i}__`;

    const header = {
      text: def.title || "",
      value: key,
      field: def.field || null,
      /**
       * `headerSort` is the ONLY sort switch — it is the key Tabulator itself
       * reads, and BreadTabulator ran `sortMode: "remote"`, so every column
       * without `headerSort: false` was backend-sortable before the migration.
       *
       * The models also carry a `sortable` key on nearly every column, but
       * Tabulator has no such option and nothing else in this codebase reads
       * it — it is inert legacy metadata. Honouring it here silently killed
       * sorting on almost the whole table, so it is deliberately ignored.
       * A column that must not sort declares `headerSort: false`.
       */
      sortable: def.headerSort === false ? false : Boolean(def.field),
      align: ALIGN_MAP[def.hozAlign] || "start",
      width: parseWidth(def.width),
      // Custom keys the rest of the stack relies on
      dataType: def.dataType,
      filterable: def.filterable !== false,
      selectable: def.selectableColumn === true,
      sticky: isFrozen(def),
      _def: def,
      /**
       * NOT user-togglable — the column picker never offers it.
       * This is emphatically NOT the same as "always rendered": several
       * non-selectable columns (the "Order By ..." sort helpers) are also
       * `showByDefault: false` and must stay hidden. Visibility is decided
       * separately, below.
       */
      notTogglable: def.selectableColumn !== true,
      // `visible: false` / `showByDefault: false` both mean "off by default".
      defaultVisible: def.showByDefault !== false && def.visible !== false,
    };

    if (header.selectable) selectable.push(header);
    if (header.defaultVisible) defaultVisible.push(key);

    if (def.editor) {
      // v1 has no cell editing. Be loud rather than silently dropping it.
      // eslint-disable-next-line no-console
      console.warn(
        `[BreadDataTable] column "${key}" declares an editor; inline editing is not supported. ` +
          `Keep this model on BreadTabulator.`
      );
    }

    all.push(header);
  });

  const visibleKeys = options.visibleKeys;
  const widths = options.widths || {};

  let stickyOffset = 0;
  let stickyIndex = 0;
  const headers = all
    /**
     * Visibility rule, in one place:
     *
     *   no stored view      -> the model's default set
     *   stored view + user-togglable column -> exactly what the view says
     *                                          (CrmView.cColumnCondition)
     *   stored view + non-togglable column  -> its model default, since the
     *                                          view has no opinion about it
     *
     * The last line is what keeps the "#", Next Action and Actions columns
     * present while still hiding the non-togglable "Order By ..." helpers.
     */
    .filter((h) => {
      if (!visibleKeys) return h.defaultVisible;
      if (h.notTogglable) return h.defaultVisible;
      return visibleKeys.indexOf(h.value) > -1;
    })
    .map((h) => {
      const width = widths[h.value] || h.width;
      const classes = ["bdt-th"];
      const cellClasses = ["bdt-td"];
      const out = Object.assign({}, h, { width });

      if (h.sticky) {
        if (!width) {
          // eslint-disable-next-line no-console
          console.warn(
            `[BreadDataTable] sticky column "${h.value}" has no explicit width; ` +
              `left offsets cannot be computed reliably.`
          );
        }
        // Offsets are cumulative over the columns that are CURRENTLY visible,
        // so they are computed here rather than baked into static CSS.
        const idx = stickyIndex++;
        classes.push("bdt-sticky", `bdt-sticky--${idx}`);
        cellClasses.push("bdt-sticky", `bdt-sticky--${idx}`);
        out.stickyIndex = idx;
        out.stickyLeft = stickyOffset;
        stickyOffset += width || 0;
      }

      out.class = classes;
      out.cellClass = cellClasses;
      return out;
    });

  return { headers, showSelect, selectable, defaultVisible, all };
}

/**
 * v-data-table's `options.sortBy` / `sortDesc` -> Tabulator's wire format.
 * @returns {Array<{field: string, dir: 'asc'|'desc'}>}
 */
export function toSortParams(sortBy, sortDesc, headers) {
  if (!Array.isArray(sortBy) || !sortBy.length) return [];
  return sortBy
    .map((key, i) => {
      const header = headers.find((h) => h.value === key);
      const field = header ? header.field || header.value : key;
      return { field, dir: sortDesc && sortDesc[i] ? "desc" : "asc" };
    })
    .filter((s) => s.field);
}

/**
 * Saved-view sort (`[{column, dir}]`) -> v-data-table options.
 */
export function fromSavedSort(savedSort, headers) {
  const sortBy = [];
  const sortDesc = [];
  (savedSort || []).forEach((s) => {
    const field = s.column || s.field;
    if (!field) return;
    const header = headers.find((h) => (h.field || h.value) === field);
    sortBy.push(header ? header.value : field);
    sortDesc.push(s.dir === "desc");
  });
  return { sortBy, sortDesc };
}
